'use client'

import { default as NextLink } from 'next/link'

import { Box, Container, Grid, Link, Stack, styled } from '@mui/material'

import { getTarget } from '@/helpers/getTarget'
import { getLinkColorStyle } from '@/helpers/linkColors'
import { useHeaderContext } from '@/hooks/useHeaderContext'

import type { FC } from 'react'
import type { TopNavigationNode } from '@/interfaces/NavigationNode'

const MenuItemWrapper = styled(Link)(({ theme }) => ({
  paddingBlock: theme.spacing(1.5),
  paddingInline: theme.spacing(1.5),
  cursor: 'pointer',
  position: 'relative',
  color: theme.palette.text.light,

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: theme.spacing(1.5),
    right: theme.spacing(1.5),
    height: 2,
    display: 'block',
    transform: 'scaleX(0)',
    transition: 'transform',
    transitionDuration: '0.3s',
    transitionTimingFunction: theme.transitions.easing.easeInOut,
    transformOrigin: 'left',
    backgroundColor: theme.palette.background.light,
  },
  '&[data-hovered="true"] ': {
    '&::after': {
      transform: 'scaleX(1)',
    },
  },
  '&:hover': {
    '&::after': {
      transform: 'scaleX(1)',
    },
  },
})) as typeof Link

const MenuItemsBarWrapper = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.background.component,
}))

const MegaMenuItem: FC<{ node: TopNavigationNode }> = ({ node }) => {
  const { toggleIsMegaMenuOpen, selectedMenuItem, isMegaMenuOpen } = useHeaderContext()
  const hovered = isMegaMenuOpen && selectedMenuItem === node.id

  return (
    <MenuItemWrapper
      data-hovered={hovered}
      onClick={toggleIsMegaMenuOpen(true, node.id, false)}
      onBlur={toggleIsMegaMenuOpen(false)}
      style={getLinkColorStyle(node)}
      variant="body2"
      underline="none"
      role="button"
    >
      {node.title}
    </MenuItemWrapper>
  )
}

const MegaMenuLink: FC<{ node: TopNavigationNode; href: string }> = ({ node, href }) => {
  const { toggleIsMegaMenuOpen } = useHeaderContext()

  return (
    <MenuItemWrapper
      component={NextLink}
      href={href}
      {...getTarget(node.openInNewTab)}
      onMouseOver={toggleIsMegaMenuOpen(false)}
      style={getLinkColorStyle(node)}
      variant="body2"
      underline="none"
    >
      {node.title}
    </MenuItemWrapper>
  )
}

interface Props {
  navigation: TopNavigationNode[]
}

export const MenuItemsBar: FC<Props> = ({ navigation }) => {
  const { menuItemsBarRef, toggleIsMegaMenuOpen } = useHeaderContext()

  return (
    <MenuItemsBarWrapper
      ref={menuItemsBarRef}
      sx={{
        display: {
          xs: 'none',
          md: 'block',
        },
      }}
    >
      {navigation.length === 0 && <Box minHeight={44} width={100} />}
      <Container disableGutters>
        <Stack sx={{ px: 1.5, overflow: 'hidden' }} onMouseLeave={toggleIsMegaMenuOpen(false)} direction="row">
          {navigation.map((node) => {
            if (node.children.length > 0) {
              return <MegaMenuItem key={node.id} node={node} />
            }

            return node.href && <MegaMenuLink key={node.id} node={node} href={node.href} />
          })}
        </Stack>
      </Container>
    </MenuItemsBarWrapper>
  )
}
